import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import type { FC } from 'react';

import type { CartCalculationOrderResponse } from '~/api/Catalog';
import FreeDeliveryProgressBar from '~/components/FreeDeliveryProgressBar/FreeDeliveryProgressBar';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '~/components/isolated/Carousel';
import Panel from '~/components/Panel/Panel';
import { useGlobal } from '~/hooks/useGlobal';
import { useSlots } from '~/pages/Checkout/useSlots';
import { cartStore, mainStore, userStore } from '~/stores';
import type DeliveryType from '~/stores/CheckoutStore/Delivery';
import { formatPriceWithCurrency } from '~/utils/formaters';

import CheckoutProductCard from '../CheckoutProductCard/CheckoutProductCard';
import DeliveryPanel from '../DeliveryPanel';

import styles from './SlotDelivery.module.scss';

interface SlotDeliveryProps {
  delivery: DeliveryType;
  order: CartCalculationOrderResponse;
  expressDeliveryEnabled: boolean | undefined;
  handleSelectDateModal: () => void;
}

const SlotDelivery: FC<SlotDeliveryProps> = ({
  delivery,
  order,
  expressDeliveryEnabled,
  handleSelectDateModal,
}) => {
  const { isMobile } = useGlobal();

  const deliveryItems = cartStore.selectedCartItems.filter(({ sku }) =>
    delivery.itemsIds.includes(sku),
  );
  const { showSelectTimeButton } = useSlots({ deliveryItems });

  const isDeliveryFree = order?.is_delivery_free;

  const freeDeliveryThreshold = order?.delivery_info.threshold;

  const leftUntilFreeDelivery = mainStore.convertPenceToPounds(
    freeDeliveryThreshold - order?.paid_without_delivery,
  );

  if (!showSelectTimeButton) {
    return null;
  }

  const carousel = isMobile ? (
    <Carousel
      opts={{
        align: 'start' as const,
        slidesToScroll: 1,
        containScroll: 'trimSnaps' as const,
      }}
      dir={userStore.dir}
      key={userStore.dir}
      className={styles.carousel}
    >
      <CarouselContent>
        {deliveryItems.map((offer) => (
          <CarouselItem className={styles.slide} key={offer.images[0]}>
            <CheckoutProductCard key={offer.id} offer={offer} />
          </CarouselItem>
        ))}
      </CarouselContent>
    </Carousel>
  ) : (
    <div className={classes([styles.desktopCarousel, styles.carousel])}>
      {deliveryItems.map((offer) => (
        <CheckoutProductCard key={offer.id} offer={offer} />
      ))}
    </div>
  );

  const list = (
    <div className={styles.list}>
      {carousel}
      {!isDeliveryFree &&
        !expressDeliveryEnabled &&
        !delivery.slotDeliveryDetails?.isAutomatic && (
          <FreeDeliveryProgressBar
            leftUntilFreeDelivery={`${formatPriceWithCurrency(
              leftUntilFreeDelivery,
            )}`}
            cssWidth={`${
              (order.paid_without_delivery / freeDeliveryThreshold) * 100
            }%`}
            className={styles.freeDeliveryProgressBar}
            lineClassName={styles.freeDeliveryProgressBarLine}
          />
        )}
    </div>
  );

  return (
    <div className={styles.slotDelivery}>
      <Panel
        icon="calendar"
        onClick={handleSelectDateModal}
        additionalContent={list}
        contentClassNames={styles.panel}
      >
        <DeliveryPanel
          type="slot"
          showPrice={!expressDeliveryEnabled}
          order={order}
          delivery={delivery}
          expressDeliveryEnabled={expressDeliveryEnabled}
        />
      </Panel>
    </div>
  );
};

export default observer(SlotDelivery);
